import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Skeleton,
} from '@mui/material';
import { Search } from 'lucide-react';
import DashGraphs from './DashGraphs';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

const SORT_OPTIONS = [
  { value: 'orders-desc', label: 'Most ordered' },
  { value: 'orders-asc', label: 'Least ordered' },
  { value: 'title-asc', label: 'Title A→Z' },
  { value: 'title-desc', label: 'Title Z→A' },
  { value: 'updated-desc', label: 'Recently updated' },
  { value: 'updated-asc', label: 'Oldest updated' },
];

const COUNT_OPTIONS = [12, 24, 50, 100];
const RANGE_OPTIONS = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
];
const LOW_STOCK_THRESHOLD = 10;

const DEFAULTS = {
  q: '',
  sort: 'orders-desc',
  show: 100,
  stock: 'all',
  range: 30,
};

function GraphsPage() {
  const [topProducts, setTopProducts] = useState([]);
  const [inventoryById, setInventoryById] = useState({});
  const [loading, setLoading] = useState(true);
  const [stockMap, setStockMap] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const search = searchParams.get('q') ?? DEFAULTS.q;
  const sortBy = searchParams.get('sort') ?? DEFAULTS.sort;
  const count = Number(searchParams.get('show')) || DEFAULTS.show;
  const stockFilter = searchParams.get('stock') ?? DEFAULTS.stock;
  const daysBack = Number(searchParams.get('range')) || DEFAULTS.range;

  const updateParam = useCallback(
    (key, value) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === undefined || value === null || value === '' || String(value) === String(DEFAULTS[key])) {
            next.delete(key);
          } else {
            next.set(key, String(value));
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    axios
      .get('https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/auth/profile', {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      })
      .catch(() => navigate('/login'));

    setLoading(true);
    axios
      .get('https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/top-selling-products?limit=250')
      .then((response) => {
        const raw = response.data || [];
        const clean = raw.filter(
          (p) =>
            p?.shopifyId &&
            typeof p?.title === 'string' &&
            p.title.trim() !== '' &&
            !p.title.includes('819150') &&
            !p.title.includes('801907') &&
            p.title.trim().toLowerCase() !== 'syringes bundle'
        );
        setTopProducts(clean);

        const ids = clean.map((p) => p.shopifyId);
        if (ids.length === 0) {
          setInventoryById({});
          return undefined;
        }

        // One batched request for every product's inventory history, instead
        // of one request per rendered graph. 90 days covers the widest range
        // toggle; DashGraphs trims to the selected window client-side.
        return axios
          .post('https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/inventory/batch', {
            ids,
            days: 90,
          })
          .then((res) => {
            const data = res.data || {};
            const map = {};
            ids.forEach((id) => {
              map[id] = data[id] || [];
            });
            setInventoryById(map);
          });
      })
      .catch((error) => console.error('Error fetching graph data:', error))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleStockReport = useCallback((shopifyId, qty) => {
    setStockMap((prev) => (prev[shopifyId] === qty ? prev : { ...prev, [shopifyId]: qty }));
  }, []);

  // Products whose whole fetched history never rises above zero — dead stock we
  // hide by default. Derived from the batch payload rather than reported back by
  // each graph, so the visible set no longer shifts with the range toggle.
  const emptyInventoryIds = useMemo(() => {
    const ids = new Set();
    Object.entries(inventoryById).forEach(([id, entries]) => {
      if (!Array.isArray(entries)) return;
      if (!entries.some((e) => Number(e.quantity ?? e.qty ?? 0) > 0)) ids.add(String(id));
    });
    return ids;
  }, [inventoryById]);

  const visibleProducts = useMemo(() => {
    let list = topProducts;

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.title.toLowerCase().includes(q));
    }

    // An explicit search always wins: if you type a SKU you should find it, even
    // when the product has been sitting at zero (or oversold) the whole time.
    if (!search.trim()) {
      list = list.filter((p) => !emptyInventoryIds.has(String(p.shopifyId)));
    }

    if (stockFilter !== 'all') {
      list = list.filter((p) => {
        const qty = stockMap[p.shopifyId];
        if (qty === undefined || qty === null) return false;
        if (stockFilter === 'out') return qty <= 0;
        if (stockFilter === 'low') return qty > 0 && qty <= LOW_STOCK_THRESHOLD;
        if (stockFilter === 'in') return qty > LOW_STOCK_THRESHOLD;
        return true;
      });
    }

    const sorted = [...list].sort((a, b) => {
      switch (sortBy) {
        case 'orders-desc':
          return (b.totalOrders || 0) - (a.totalOrders || 0);
        case 'orders-asc':
          return (a.totalOrders || 0) - (b.totalOrders || 0);
        case 'title-asc':
          return a.title.localeCompare(b.title);
        case 'title-desc':
          return b.title.localeCompare(a.title);
        case 'updated-desc':
          return new Date(b.lastUpdated || 0) - new Date(a.lastUpdated || 0);
        case 'updated-asc':
          return new Date(a.lastUpdated || 0) - new Date(b.lastUpdated || 0);
        default:
          return 0;
      }
    });

    return sorted.slice(0, count);
  }, [topProducts, search, sortBy, count, stockFilter, stockMap, emptyInventoryIds]);

  return (
    <Box className="fade-in" sx={{ width: '100%' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 0.5 }}>
        Graphs
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Visual overview of top-selling product performance
      </Typography>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        <TextField
          variant="outlined"
          placeholder="Search products..."
          value={search}
          onChange={(e) => updateParam('q', e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} style={{ opacity: 0.4 }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flex: 1, maxWidth: 400 }}
        />

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            label="Sort by"
            value={sortBy}
            onChange={(e) => updateParam('sort', e.target.value)}
          >
            {SORT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Show</InputLabel>
          <Select
            label="Show"
            value={count}
            onChange={(e) => updateParam('show', e.target.value)}
          >
            {COUNT_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={stockFilter}
          exclusive
          size="small"
          onChange={(_, v) => v && updateParam('stock', v)}
          aria-label="Stock filter"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="in">In stock</ToggleButton>
          <ToggleButton value="low">Low</ToggleButton>
          <ToggleButton value="out">Out</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={daysBack}
          exclusive
          size="small"
          onChange={(_, v) => v && updateParam('range', v)}
          aria-label="Date range"
        >
          {RANGE_OPTIONS.map((r) => (
            <ToggleButton key={r.value} value={r.value}>{r.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
        {loading
          ? 'Loading…'
          : `Showing ${visibleProducts.length} of ${topProducts.length} products · last ${daysBack} days`}
      </Typography>

      {loading ? (
        <Grid container spacing={2.5}>
          {Array.from({ length: count }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={i}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <Skeleton variant="text" width="70%" sx={{ mx: 'auto', mb: 1 }} />
                <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 1 }} />
              </Box>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2.5} alignItems="stretch">
          {visibleProducts.map((product) => (
            <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={product.shopifyId}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  cursor: 'pointer',
                  transition: 'all 0.12s ease',
                  height: '100%',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                }}
              >
                <DashGraphs
                  productId={product.shopifyId}
                  productTitle={product.title}
                  daysBack={daysBack}
                  inventoryData={inventoryById[product.shopifyId] || []}
                  onInventoryLoaded={handleStockReport}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && visibleProducts.length === 0 && topProducts.length > 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No products match your filters.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default GraphsPage;
